import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import { useEmployeeRole } from './useEmployeeRole';

/**
 * Returns the set of shop slugs the current employee can access.
 *   - Owner: returns null (sentinel meaning "no filter — see everything").
 *   - Everyone else: array of slugs sourced from
 *     `employeeIdentity.accessible_shops` (preferred) or
 *     `employeeIdentity.accessible_brands` (Phase 8-E alias) — whichever the
 *     backend populated. An empty array means "scoped to nothing yet" (e.g. a
 *     newly created analyst with no shops assigned).
 *
 * Returning null vs. [] is load-bearing — callers MUST distinguish them or
 * they'll accidentally hide everything from owners.
 */
export function useAccessibleShops(): string[] | null {
  const role = useEmployeeRole();
  const identity = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity
  );

  if (role === 'owner') return null;
  if (!identity) return [];

  // Prefer the legacy shape; fall back to the Phase 8-E brand alias when only
  // that is present. Both fields carry the same scope, just with different
  // field names.
  if (identity.accessible_shops && identity.accessible_shops.length > 0) {
    return identity.accessible_shops
      .map((s) => s.slug ?? s.brand_slug)
      .filter((slug): slug is string => Boolean(slug));
  }
  if (identity.accessible_brands && identity.accessible_brands.length > 0) {
    return identity.accessible_brands
      .map((b) => b.brand_slug)
      .filter((slug): slug is string => Boolean(slug));
  }
  return [];
}

/**
 * Filter helper for shop / shop-scoped lists. Owners (null) see everything.
 * Non-owners only see items whose `slug` or `shop_slug` is in the allow-list.
 *
 * Items without any slug field are dropped for non-owners — fail closed.
 */
export function filterByAccessibleShops<
  T extends { slug?: string; shop_slug?: string }
>(items: T[], accessibleSlugs: string[] | null): T[] {
  if (accessibleSlugs === null) return items; // owner sees all
  return items.filter((i) => {
    const slug = i.slug ?? i.shop_slug;
    return slug ? accessibleSlugs.includes(slug) : false;
  });
}
